import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { makeStyles } from "@mui/styles";
import styles from "../../styles/loginPage";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import image from "../../assets/img/signIn-bg2.webp";
import { api } from "common";
import { useTranslation } from "react-i18next";
// Import verifyPasswordResetCode from firebase/auth
import { verifyPasswordResetCode, getAuth } from "firebase/auth";
import Availability from "components/Availability";
import HomeFooter from "components/Footer/HomeFooter";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "transparent",
    "&:hover": { backgroundColor: "transparent" },
    "&.Mui-focused": { backgroundColor: "transparent" },
    // "&.Mui-disabled": { backgroundColor: "#000" },
    "&:before": { borderBottom: "1px solid rgba(255, 255, 255, 0.7)" },
    "&:after": { borderBottom: "2px solid #fff" },
    "&:hover:not(.Mui-disabled):before": { borderBottom: "2px solid #fff" },
  },
  "& .MuiFilledInput-input": {
    padding: "8px 0",
    color: "#fff",
    fontSize: "1.1rem",
    backgroundColor: "transparent",
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset",
      WebkitTextFillColor: "#fff",
      caretColor: "#fff",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  "& .MuiFormLabel-root": {
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 500,
    transform: "none",
    position: "relative",
    marginBottom: "8px",
  },
  "& .MuiFormLabel-root.Mui-focused": { color: "#fff" },
}));

const useStyles = makeStyles(styles);

const ResetPassword = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const classes = useStyles();
  const { ...rest } = props;

  const { t } = useTranslation();

  const auth = getAuth();

  useEffect(() => {
    const oobCode = searchParams.get("oobCode");

    if (!oobCode) {
      navigate("/404", { replace: true });
      return;
    }

    const verifyCode = async () => {
      setVerifying(true);
      try {
        await verifyPasswordResetCode(auth, oobCode);
        setTokenValid(true);
      } catch (error) {
        console.error("Reset code verification failed:", error);

        navigate("/404", { replace: true });
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [navigate, searchParams, t, auth]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, t("password_min"))
      .matches(/[a-z]/, t("password_lowercase"))
      .matches(/[A-Z]/, t("password_uppercase"))
      .matches(/[0-9]/, t("password_number"))
      .matches(/^\S*$/, t("password_must_not_contain_whitespaces"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t("password_special"))
      .required(t("password_required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError("");

      const oobCode = searchParams.get("oobCode");

      try {
        dispatch(api.confirmPassword(values.password, oobCode));
        setSuccess(true);
        navigate("/login");
      } catch (error) {
        setError(
          error?.message || "Failed to reset password. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!tokenValid) {
    return null;
  }

  return (
    <div>
      <div
        style={{
          backgroundImage: "url(" + image + ")",
          backgroundSize: "cover",
          backgroundPosition: "top center",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Header
          absolute
          color="transparent"
          rightLinks={<HeaderLinks />}
          {...rest}
        />
        <div className={classes.pageHeader}>
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
              width: "100%",
              maxWidth: 500,
              marginTop: "15vh",
              // mx: "auto",
              zIndex: 2,
              p: 4,
              borderRadius: 4,
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{ color: "#fff", mb: 2, fontWeight: 600 }}
            >
              {t("resetPassword")}
            </Typography>

            <Typography
              align="center"
              sx={{ color: "rgb(255, 255, 255)", mb: 4 }}
            >
              {t("enterNewPassword")}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  position: "fixed",
                  top: 16,
                  right: 16,
                  zIndex: 9999,
                }}
              >
                {t("passwordResetSuccess")}
              </Alert>
            )}

            <Stack spacing={3}>
              <Box>
                <StyledTextField
                  fullWidth
                  label={t(`password`)}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="filled"
                  autoComplete="new-password"
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  label={t(`confirm_password`)}
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="filled"
                  autoComplete="new-password"
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  sx={{ mt: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!formik.isValid || !formik.dirty || isLoading}
                sx={{
                  bgcolor: "#5BAA59",
                  color: "#fff",
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1.1rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#6BB566" },
                  "&:disabled": { bgcolor: "#5BAA59", opacity: 0.5 },
                }}
              >
                {isLoading ? "Resetting..." : t(`resetPassword`)}
              </Button>
            </Stack>
          </Box>
        </div>
      </div>

      <Availability />
      <HomeFooter />
    </div>
  );
};

export default ResetPassword;
